// src/tabs/logements/LivretQrDownloadButton.jsx — Phase A.6 (ADR-118)
//                                                  + Sprint A.9.2 (ADR-131)
//
// Bouton "📄 QR PDF" dans LivretModulesEditor. Génère et télécharge le PDF
// imprimable pour affichage dans le logement (proprio → voyageur).
//
// Patterns réutilisés :
// - qrLoading state machine identique à previewLoading (LivretModulesEditor.jsx:54, F-1)
// - createObjectURL + setTimeout revoke 100ms (Safari compat, pessimist F-3)
//
// Sprint A.9.2 PR-A92-5 (ADR-131) : récupère la cover photo logement via
// endpoint admin /api/beds24-mapping?action=logement_gallery_cover puis la
// passe à generateLivretQrPdf() pour insertion AVANT QR code. Mode dégradé
// propre : fetch échec → coverPhotoUrl=null → PDF généré sans cover (F-CRIT-2).

import { useState } from "react";
import { DS } from "../../lib/ui";
import { QrCode } from "lucide-react";
import { supabase } from "../../supabase";
import { generateLivretQrPdf } from "../../lib/livret-qr-pdf";

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

export default function LivretQrDownloadButton({ slug, title, logement_id }) {
  const [qrLoading, setQrLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDisabled = qrLoading || !slug;

  async function handleDownload() {
    if (qrLoading) return; // Pessimist F-1 garde double-clic
    setError(null);
    setQrLoading(true);
    try {
      // Sprint A.9.2 (ADR-131) : fetch cover photo (best-effort).
      // F-CRIT-2 : endpoint serveur requireAdmin (RLS livret_gallery admin-only).
      let coverPhotoUrl = null;
      if (logement_id) {
        try {
          const headers = await getAuthHeaders();
          const resp = await fetch(
            `/api/beds24-mapping?action=logement_gallery_cover&logement_id=${encodeURIComponent(logement_id)}`,
            { method: "GET", headers },
          );
          if (resp.ok) {
            const json = await resp.json();
            coverPhotoUrl = json?.cover?.public_url || null;
          }
        } catch (_) {
          // Fallback null OK : PDF généré sans cover (mode dégradé propre).
        }
      }

      const blob = await generateLivretQrPdf({ slug, title, coverPhotoUrl });
      const url = URL.createObjectURL(blob);
      const filename = `livret-qr-${slug}.pdf`;
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Pessimist F-3 : setTimeout 100ms pour Safari compat
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (e) {
      setError(e.message || "Erreur génération PDF");
    } finally {
      setQrLoading(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleDownload}
        disabled={isDisabled}
        aria-label="Télécharger le QR PDF imprimable"
        aria-busy={qrLoading}
        title={
          !slug
            ? "Slug livret manquant"
            : qrLoading
              ? "Génération en cours..."
              : "Télécharger le PDF imprimable du QR"
        }
        className="fbz-press"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "8px 14px",
          background: DS.card,
          color: DS.marine,
          border: `0.5px solid ${DS.borderHover}`,
          borderRadius: DS.radiusControl,
          fontSize: 12,
          fontWeight: 600,
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
          fontFamily: DS.fontBody,
          marginBottom: 14,
        }}
      >
        <QrCode size={14} strokeWidth={1.8} /> {qrLoading ? "Génération..." : "QR PDF"}
      </button>
      {error && (
        <div
          style={{
            marginTop: 6,
            padding: "6px 10px",
            background: DS.dangerBg,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: 6,
            color: DS.dangerText,
            fontSize: 11,
          }}
        >
          {error}
        </div>
      )}
    </>
  );
}
